import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Currency, CurrencyStatus } from '../../../../core/models/currency';
import { CurrencyService } from '../../../../core/services/currency.service';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'em-currency-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    DropdownModule,
    ButtonModule,
    ToastModule,
    TranslatePipe
  ],
  templateUrl: './currency-form.component.html',
  styleUrls: ['./currency-form.component.scss'],
  providers: [MessageService]
})
export class CurrencyFormComponent implements OnInit {
  currencyForm!: FormGroup;
  isEditMode = false;
  isLoading = false;
  translations: any = {};

  statusOptions: { label: string; value: CurrencyStatus }[] = [];
  private destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private currencyService: CurrencyService,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private messageService: MessageService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.buildStatusOptions();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildStatusOptions());
    this.loadTranslations();
    this.initForm();

    if (this.config.data && this.config.data.mode === 'edit') {
      this.isEditMode = true;
      this.currencyForm.patchValue(this.config.data);
    }
  }

  private buildStatusOptions(): void {
    this.statusOptions = [
      { label: this.translate.instant('layout.configurations.currency.form.active'), value: CurrencyStatus.ACTIVE },
      { label: this.translate.instant('layout.configurations.currency.form.inactive'), value: CurrencyStatus.INACTIVE }
    ];
  }

  loadTranslations(): void {
    this.translate.get([
      'layout.configurations.currency.form.currencyName',
      'layout.configurations.currency.form.status',
      'layout.configurations.currency.form.required',
      'layout.configurations.currency.form.maxLength',
      'layout.configurations.currency.form.invalidName',
      'layout.configurations.currency.save.success.title',
      'layout.configurations.currency.save.success.detail',
      'layout.configurations.currency.save.error.title',
      'layout.configurations.currency.save.error.detail',
      'layout.configurations.currency.save.error.exists'
    ]).subscribe((translations) => {
      this.translations = translations;
    });
  }

  
  private currencyNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = (control.value || '').trim();
      if (!value) {
        return null;
      }

      const allowedCharsPattern = /^[A-Za-z0-9\s.'()-]+$/;
      const hasLetterPattern = /[A-Za-z]/;

      if (!allowedCharsPattern.test(value) || !hasLetterPattern.test(value)) {
        return { invalidCurrencyName: true };
      }

      return null;
    };
  }

  initForm(): void {
    this.currencyForm = this.fb.group({
      id: [null],
      currencyName: ['', [Validators.required, Validators.maxLength(100), this.currencyNameValidator()]],
      status: [CurrencyStatus.ACTIVE, Validators.required]
    });
  }

  onSave(): void {
    if (this.currencyForm.invalid) {
      Object.values(this.currencyForm.controls).forEach((control) => {
        control.markAsTouched();
      });
      return;
    }

    this.isLoading = true;
    const currency: Currency = {
      ...this.currencyForm.value,
      currencyName: this.currencyForm.value.currencyName.trim()
    };

    const request = this.isEditMode
      ? this.currencyService.update(this.config.data.id, currency)
      : this.currencyService.create(currency);

    request.subscribe({
      next: () => {
        this.ref.close({ type: 'success' });
      },
      error: (error) => {
        console.error('Error saving currency:', error);
        const detail = error.status === 409 || error.error?.message?.includes('already exists')
          ? this.translate.instant('layout.configurations.currency.save.error.exists')
          : this.translate.instant('layout.configurations.currency.save.error.detail');

        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('layout.configurations.currency.save.error.title'),
          detail: detail,
          life: 4000
        });
        this.isLoading = false;
      }
    });
  }

  onCancel(): void {
    this.ref.close(null);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.currencyForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getErrorMessage(fieldName: string): string {
    const field = this.currencyForm.get(fieldName);
    if (field?.hasError('required')) {
      return this.translate.instant('layout.configurations.currency.form.required');
    }
    if (field?.hasError('maxlength')) {
      return this.translate.instant('layout.configurations.currency.form.maxLength');
    }
    if (field?.hasError('invalidCurrencyName')) {
      return this.translate.instant('layout.configurations.currency.form.invalidName');
    }
    return '';
  }
}